import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Folder, File, ChevronRight, ChevronDown, Download, Upload, Edit2,
  FolderInput, RefreshCw, FileText, ClipboardCheck, HardDrive, X, Image as ImageIcon,
  FolderPlus, Trash2, Search, Copy, Star, PieChart, BarChart2, Check,
} from 'lucide-react';
import { SpanwiseLoadsPanel } from './JobManagement';
import useIsMobile from './useIsMobile';

const MNT_ROOT = '/mnt/data0/shared';
const SHARED_PREFIX = '/mnt/data0/shared';
const PINS_KEY = 'docs_pinned_folders';

const bulkBtn = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6,
  border: `1px solid ${color}55`, background: `${color}18`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
});

const loadPins = () => {
  try { return JSON.parse(localStorage.getItem(PINS_KEY) || '[]'); } catch { return []; }
};
const savePins = (arr) => {
  try { localStorage.setItem(PINS_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
};

const fmtSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};
const fmtTime = (ts) => (ts ? new Date(ts * 1000).toLocaleString() : '');
const rel = (p) => p.replace(/^\/+/, '');

// file-kind badge styling (mirrors backend _classify_file)
const KIND = {
  'case-def': { label: 'Case def', color: '#f472b6' },
  case: { label: 'Case', color: '#ec4899' },
  mesh: { label: 'Mesh', color: '#f59e0b' },
  results: { label: 'Results', color: '#22d3ee' },
  errors: { label: 'Errors', color: '#f87171' },
  log: { label: 'Log', color: '#94a3b8' },
  script: { label: 'Script', color: '#34d399' },
  nastran: { label: 'Nastran', color: '#a78bfa' },
  image: { label: 'Image', color: '#38bdf8' },
  table: { label: 'Table', color: '#60a5fa' },
  control: { label: 'Control', color: '#64748b' },
  data: { label: 'Data', color: '#9ca3af' },
  file: { label: 'File', color: '#64748b' },
};

function KindBadge({ kind }) {
  const k = KIND[kind];
  if (!k || !k.label) return null;
  return <span style={{
    fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    color: k.color, border: `1px solid ${k.color}55`, borderRadius: 4, padding: '1px 5px',
  }}>{k.label}</span>;
}

function DirOverview({ info }) {
  if (!info) return null;
  const chip = (label, val) => (
    <span style={{ fontSize: 11, color: '#9ca3af' }}><span style={{ color: '#64748b' }}>{label} </span><b style={{ color: '#e5e7eb' }}>{val}</b></span>
  );
  return (
    <div style={{ backgroundColor: '#0b1322', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: info.isCase || Object.values(info.contents || {}).some(Boolean) ? 10 : 0 }}>
        {chip('Files', info.fileCount)}
        {chip('Folders', info.dirCount)}
        {chip('Size', fmtSize(info.totalSize))}
        {info.isCase && info.mach != null && chip('Mach', info.mach)}
        {info.isCase && info.aoa != null && chip('α', `${info.aoa}°`)}
        {info.jobStatus && <span style={{ fontSize: 11, color: '#34d399' }}><span style={{ color: '#64748b' }}>solver </span><b>{info.jobStatus}</b></span>}
        {info.biggest && chip('Largest', `${info.biggest.name} (${fmtSize(info.biggest.size)})`)}
      </div>
      {(info.isCase || Object.values(info.contents || {}).some(Boolean)) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {Object.entries(info.contents || {}).map(([label, present]) => (
            <span key={label} style={{ fontSize: 11, color: present ? '#86efac' : '#475569', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 700 }}>{present ? '✓' : '–'}</span>{label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── left nav: lazy folder tree ─────────────────────────
function TreeNode({ node, depth, selectedPath, onSelect, refreshKey }) {
  const [open, setOpen] = useState(depth < 2);       // auto-open /mnt and data0
  const [children, setChildren] = useState(null);    // null = not yet loaded
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/docs/list', { params: { path: node.path } });
      setChildren(r.data?.success ? r.data.entries.filter((e) => e.type === 'dir') : []);
    } catch { setChildren([]); }
    finally { setLoading(false); }
  }, [node.path]);

  useEffect(() => { if (open && children === null) load(); }, [open, children, load]);
  // Reload an open node when the tree is asked to refresh (after a write op).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [refreshKey]);

  const selected = selectedPath === node.path;
  return (
    <div>
      <div
        onClick={() => onSelect(node.path)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
          padding: '3px 6px', paddingLeft: 6 + depth * 14, borderRadius: 6,
          backgroundColor: selected ? 'rgba(59,130,246,0.18)' : 'transparent',
          color: selected ? '#fff' : '#cbd5e1', fontSize: 13, whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = '#1e293b'; }}
        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <span onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          style={{ display: 'inline-flex', width: 14 }}>
          {loading ? <RefreshCw style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
            : open ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
        </span>
        {depth === 0
          ? <HardDrive style={{ width: 14, height: 14, color: '#38bdf8' }} />
          : <Folder style={{ width: 14, height: 14, color: '#f59e0b' }} />}
        <span>{node.name}</span>
      </div>
      {open && children && children.map((c) => (
        <TreeNode key={c.path} node={c} depth={depth + 1}
          selectedPath={selectedPath} onSelect={onSelect} refreshKey={refreshKey} />
      ))}
    </div>
  );
}

// ───────────────────────── Visuals: PowerVIZ headless render ──────────────────
function VisualsSection({ dir }) {
  const [data, setData] = useState(null);   // { hasCase, images, render }
  const [rendering, setRendering] = useState(false);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/docs/renders', { params: { path: dir } });
      if (r.data?.success) setData(r.data);
    } catch { /* ignore */ }
  }, [dir]);

  useEffect(() => { setErr(null); setData(null); load(); return () => clearInterval(pollRef.current); }, [dir, load]);

  const render = useCallback(async () => {
    setErr(null); setRendering(true);
    try {
      const res = await axios.post('/api/docs/render', { path: dir });
      if (!res.data?.success && res.data?.status !== 'running') {
        setErr(res.data?.error || 'render failed'); setRendering(false); return;
      }
      let ticks = 0;
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        ticks += 1;
        try {
          const r = await axios.get('/api/docs/renders', { params: { path: dir } });
          if (r.data?.success) {
            setData(r.data);
            const st = r.data.render || {};
            if (st.status === 'done') { setRendering(false); clearInterval(pollRef.current); }
            else if (st.status === 'error') { setErr(st.error || 'render failed'); setRendering(false); clearInterval(pollRef.current); }
          }
        } catch { /* keep polling */ }
        if (ticks > 300) { clearInterval(pollRef.current); setRendering(false); }
      }, 5000);
    } catch (e) { setErr(e.response?.data?.error || e.message); setRendering(false); }
  }, [dir]);

  if (!data) return null;
  const images = data.images || [];
  const dl = (p) => `/api/docs/download?path=${encodeURIComponent(p)}`;
  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <ImageIcon style={{ width: 15, height: 15, color: '#38bdf8' }} />
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Visuals</span>
        <span style={{ color: '#6b7280', fontSize: 11 }}>PowerVIZ — saved viewpoints</span>
        <div style={{ flex: 1 }} />
        {data.hasCase && (
          <button onClick={render} disabled={rendering} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
            border: 'none', cursor: rendering ? 'default' : 'pointer', fontSize: 12, fontWeight: 600,
            backgroundColor: rendering ? '#1e293b' : '#2563eb', color: rendering ? '#93c5fd' : '#fff',
          }}>
            {rendering
              ? <><RefreshCw style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> Rendering…</>
              : (images.length ? 'Re-render' : 'Render views')}
          </button>
        )}
      </div>
      {!data.hasCase && <div style={{ color: '#6b7280', fontSize: 12 }}>No <code>.cdi</code> in this folder to render.</div>}
      {rendering && <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 8 }}>PowerVIZ is rendering the case's saved viewpoints on the post-proc cluster (node16) — a few minutes; consumes a PowerVIZ license.</div>}
      {err && <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {images.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {images.map((im) => (
            <div key={im.path}>
              <a href={dl(im.path)} target="_blank" rel="noreferrer">
                <img src={dl(im.path)} alt={im.name} style={{ width: '100%', borderRadius: 8, border: '1px solid #1e293b', background: '#fff' }} />
              </a>
              <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 3, fontFamily: 'monospace' }}>{im.name}</div>
            </div>
          ))}
        </div>
      ) : data.hasCase && !rendering ? (
        <div style={{ color: '#6b7280', fontSize: 12 }}>No rendered images yet — click <b>Render views</b> to capture the case's saved viewpoints.</div>
      ) : null}
    </div>
  );
}

// ───────────────────────── Post tab (reuses existing post-proc) ────────────────
function PostPane({ dir }) {
  const [caseChk, setCaseChk] = useState(null);
  const [spanwise, setSpanwise] = useState(null);
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const pollRef = useRef(null);

  const underShared = dir === SHARED_PREFIX || dir.startsWith(SHARED_PREFIX + '/');

  const loadSpanwise = useCallback(async () => {
    try {
      const r = await axios.get(`/api/jobs/${rel(dir)}/spanwise`);
      if (r.data?.success) { setSpanwise(r.data.spanwiseLoads); setStatus(r.data.postprocStatus); }
    } catch { /* not a case dir */ }
  }, [dir]);

  useEffect(() => {
    setCaseChk(null); setSpanwise(null); setStatus(null); setGenError(null);
    if (!underShared) return;
    axios.get('/api/case-check', { params: { path: dir } })
      .then((r) => { if (r.data?.success) setCaseChk(r.data.result); }).catch(() => {});
    loadSpanwise();
    return () => clearInterval(pollRef.current);
  }, [dir, underShared, loadSpanwise]);

  const generate = useCallback(async () => {
    setGenError(null); setGenerating(true);
    try {
      const res = await axios.post(`/api/jobs/${rel(dir)}/spanwise/generate`);
      if (!res.data?.success && res.data?.status !== 'running') {
        setGenError(res.data?.error || 'generate failed'); setGenerating(false); return;
      }
      let ticks = 0;
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        ticks += 1;
        try {
          const r = await axios.get(`/api/jobs/${rel(dir)}/spanwise`);
          if (r.data?.success) {
            setSpanwise(r.data.spanwiseLoads); setStatus(r.data.postprocStatus);
            const g = r.data.generate || {};
            if (r.data.spanwiseLoads?.available) { setGenerating(false); clearInterval(pollRef.current); }
            else if (g.status === 'error') { setGenError(g.error || 'post-proc failed'); setGenerating(false); clearInterval(pollRef.current); }
          }
        } catch { /* keep polling */ }
        if (ticks > 240) { clearInterval(pollRef.current); setGenerating(false); }
      }, 5000);
    } catch (e) { setGenError(e.response?.data?.error || e.message); setGenerating(false); }
  }, [dir]);

  if (!underShared) {
    return <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>
      Post-processing applies to case directories under <code>/mnt/data0/shared</code>. Navigate into a case folder.
    </div>;
  }

  const SEV = { fail: '#f87171', warn: '#facc15', pass: '#34d399', info: '#60a5fa' };
  return (
    <div style={{ padding: 4 }}>
      {caseChk && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <ClipboardCheck style={{ width: 15, height: 15, color: '#3b82f6' }} />
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Case Readiness</span>
            <span style={{ fontSize: 11, color: SEV[caseChk.overall] || '#9ca3af' }}>
              {caseChk.summary.pass} pass · {caseChk.summary.warn} warn · {caseChk.summary.fail} fail
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {caseChk.checks.slice(0, 6).map((c) => (
              <div key={c.id} style={{ fontSize: 12, color: '#cbd5e1' }}>
                <span style={{ color: SEV[c.severity] || '#9ca3af', fontWeight: 700, textTransform: 'uppercase', fontSize: 10, marginRight: 8 }}>{c.severity}</span>
                <b>{c.label}:</b> {c.detail}
              </div>
            ))}
          </div>
        </div>
      )}
      <SpanwiseLoadsPanel spanwise={spanwise} status={status}
        onGenerate={generate} generating={generating} genError={genError} />
      <VisualsSection dir={dir} />
      {!caseChk && !spanwise?.available && !status?.canGenerate && (
        <div style={{ color: '#6b7280', fontSize: 13, padding: 12 }}>No post-processing data or inputs found in this folder.</div>
      )}
    </div>
  );
}

// ───────────────────────── main Docs page ─────────────────────────────────────
export default function Docs() {
  const isMobile = useIsMobile();
  const [currentPath, setCurrentPath] = useState(SHARED_PREFIX);
  const [listing, setListing] = useState(null);
  const [dirInfo, setDirInfo] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('files');       // files | post
  const [viewer, setViewer] = useState(null);        // opened file preview
  const [busy, setBusy] = useState(false);
  const [treeRefresh, setTreeRefresh] = useState(0);
  const [selected, setSelected] = useState(() => new Set());   // multi-select paths
  const [pins, setPins] = useState(loadPins);                   // pinned folder paths
  const [showUsage, setShowUsage] = useState(false);            // disk-usage panel
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState(null);             // null=not searching
  const [searching, setSearching] = useState(false);
  const fileInput = useRef(null);

  const listingReq = useRef(0);
  const loadListing = useCallback(async (path) => {
    const id = ++listingReq.current;   // latest-request-wins across rapid nav
    setError(null);
    try {
      const r = await axios.get('/api/docs/list', { params: { path } });
      if (id !== listingReq.current) return;   // superseded by a newer dir
      if (r.data?.success) setListing(r.data);
      else setError(r.data?.error || 'failed to list');
    } catch (e) { if (id === listingReq.current) setError(e.response?.data?.error || e.message); }
  }, []);

  useEffect(() => {
    loadListing(currentPath); setViewer(null); setDirInfo(null); setSelected(new Set());
    let cancelled = false;
    axios.get('/api/docs/dirinfo', { params: { path: currentPath } })
      .then((r) => { if (!cancelled && r.data?.success) setDirInfo(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentPath, loadListing]);

  const viewerReq = useRef(0);
  const openFile = useCallback(async (path) => {
    const id = ++viewerReq.current;
    setViewer({ loading: true, path });
    try {
      const r = await axios.get('/api/docs/view', { params: { path } });
      if (id !== viewerReq.current) return;   // a newer file open superseded this
      setViewer(r.data?.success ? r.data : { error: r.data?.error || 'failed', path });
    } catch (e) { if (id === viewerReq.current) setViewer({ error: e.response?.data?.error || e.message, path }); }
  }, []);

  const afterWrite = useCallback(() => { loadListing(currentPath); setTreeRefresh((k) => k + 1); }, [currentPath, loadListing]);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('path', currentPath);
      const r = await axios.post('/api/docs/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!r.data?.success) setError(r.data?.error || 'upload failed'); else afterWrite();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [currentPath, afterWrite]);

  const doRename = useCallback(async (entry) => {
    const newName = window.prompt(`Rename "${entry.name}" to:`, entry.name);
    if (!newName || newName === entry.name) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/docs/rename', { path: entry.path, newName });
      if (!r.data?.success) setError(r.data?.error || 'rename failed'); else afterWrite();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [afterWrite]);

  const doMove = useCallback(async (entry) => {
    const destDir = window.prompt(`Move "${entry.name}" into directory (absolute path under /mnt):`, currentPath);
    if (!destDir) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/docs/move', { path: entry.path, destDir });
      if (!r.data?.success) setError(r.data?.error || 'move failed'); else afterWrite();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [currentPath, afterWrite]);

  const doMkdir = useCallback(async () => {
    const name = window.prompt('New folder name:', '');
    if (!name) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/docs/mkdir', { path: currentPath, name });
      if (!r.data?.success) setError(r.data?.error || 'create failed'); else afterWrite();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [currentPath, afterWrite]);

  const doDelete = useCallback(async (paths) => {
    const list = Array.isArray(paths) ? paths : [paths];
    if (list.length === 0) return;
    const label = list.length === 1 ? `"${list[0].split('/').pop()}"` : `${list.length} items`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/docs/delete', { paths: list });
      if (!r.data?.success) setError(r.data?.error || 'delete failed');
      else {
        const failed = (r.data.results || []).filter((x) => !x.ok);
        if (failed.length) setError(`${failed.length} could not be deleted: ${failed.map((f) => f.error).join(', ')}`);
        setSelected(new Set()); afterWrite();
      }
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [afterWrite]);

  const doBulkMove = useCallback(async (paths) => {
    const destDir = window.prompt(`Move ${paths.length} item(s) into directory (absolute path under /mnt):`, currentPath);
    if (!destDir) return;
    setBusy(true);
    let fails = 0;
    try {
      for (const p of paths) {
        try {
          const r = await axios.post('/api/docs/move', { path: p, destDir });
          if (!r.data?.success) fails++;
        } catch { fails++; }
      }
      if (fails) setError(`${fails} of ${paths.length} could not be moved.`);
      setSelected(new Set()); afterWrite();
    } finally { setBusy(false); }
  }, [currentPath, afterWrite]);

  const togglePin = useCallback((path) => {
    setPins((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      savePins(next); return next;
    });
  }, []);

  const searchReq = useRef(0);
  const runSearch = useCallback(async (q) => {
    const query = (q ?? searchQ).trim();
    if (query.length < 2) { setSearchRes(null); return; }
    const id = ++searchReq.current;
    setSearching(true); setError(null);
    try {
      const r = await axios.get('/api/docs/search', { params: { q: query, path: MNT_ROOT } });
      if (id !== searchReq.current) return;
      if (r.data?.success) setSearchRes(r.data);
      else { setSearchRes(null); setError(r.data?.error || 'search failed'); }
    } catch (e) { if (id === searchReq.current) setError(e.response?.data?.error || e.message); }
    finally { if (id === searchReq.current) setSearching(false); }
  }, [searchQ]);

  const clearSearch = useCallback(() => { setSearchQ(''); setSearchRes(null); searchReq.current++; }, []);

  const toggleSel = useCallback((path) => {
    setSelected((s) => { const n = new Set(s); n.has(path) ? n.delete(path) : n.add(path); return n; });
  }, []);

  // breadcrumb segments from /mnt down to currentPath
  const crumbs = (() => {
    const parts = currentPath.split('/').filter(Boolean); // ['mnt','data0','shared',...]
    const out = []; let acc = '';
    parts.forEach((p) => { acc += '/' + p; out.push({ name: p, path: acc }); });
    return out;
  })();

  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16,
      height: isMobile ? 'auto' : 'calc(100vh - 190px)', minHeight: isMobile ? 0 : 420,
    }}>
      {/* Left: folder tree */}
      <div style={{
        width: isMobile ? '100%' : 300, flexShrink: 0, backgroundColor: '#0f172a', borderRadius: 12,
        border: '1px solid #1e293b', padding: 10, overflow: 'auto',
        maxHeight: isMobile ? '38vh' : undefined,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: '#6b7280', textTransform: 'uppercase', margin: '2px 6px 8px' }}>Shared Storage</div>
        <TreeNode node={{ name: 'shared', path: MNT_ROOT }} depth={0}
          selectedPath={currentPath} onSelect={setCurrentPath} refreshKey={treeRefresh} />
      </div>

      {/* Right: reading pane */}
      <div style={{ flex: 1, minHeight: isMobile ? '60vh' : 0, display: 'flex', flexDirection: 'column', backgroundColor: '#0f172a', borderRadius: 12, border: '1px solid #1e293b', overflow: 'hidden' }}>
        {/* header: Files/Post toggle + breadcrumb + upload */}
        <div style={{ borderBottom: '1px solid #1e293b', padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {['files', 'post'].map((m) => (
              <button key={m} onClick={() => setMode(m)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6,
                border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                backgroundColor: mode === m ? '#2563eb' : '#1e293b', color: mode === m ? '#fff' : '#9ca3af',
              }}>
                {m === 'files' ? <FileText style={{ width: 13, height: 13 }} /> : <ClipboardCheck style={{ width: 13, height: 13 }} />}
                {m === 'files' ? 'Files' : 'Post'}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            {mode === 'files' && (
              <>
                <button onClick={() => setShowUsage((v) => !v)} title="Disk usage" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6,
                  border: '1px solid #334155', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  backgroundColor: showUsage ? '#1e293b' : '#0b1322', color: '#fbbf24',
                }}>
                  <PieChart style={{ width: 13, height: 13 }} /> Disk
                </button>
                <button onClick={doMkdir} disabled={busy} title="New folder" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6,
                  border: '1px solid #334155', cursor: busy ? 'default' : 'pointer', fontSize: 12, fontWeight: 600,
                  backgroundColor: '#0b1322', color: '#34d399',
                }}>
                  <FolderPlus style={{ width: 13, height: 13 }} /> New folder
                </button>
                <input ref={fileInput} type="file" style={{ display: 'none' }}
                  onChange={(e) => { doUpload(e.target.files[0]); e.target.value = ''; }} />
                <button onClick={() => fileInput.current?.click()} disabled={busy} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6,
                  border: '1px solid #334155', cursor: busy ? 'default' : 'pointer', fontSize: 12, fontWeight: 600,
                  backgroundColor: '#0b1322', color: '#93c5fd',
                }}>
                  {busy ? <RefreshCw style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Upload style={{ width: 13, height: 13 }} />}
                  Upload here
                </button>
              </>
            )}
          </div>

          {/* search across the whole share */}
          {mode === 'files' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 460 }}>
                <Search style={{ position: 'absolute', left: 9, top: 8, width: 14, height: 14, color: '#64748b' }} />
                <input value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); if (e.key === 'Escape') clearSearch(); }}
                  placeholder="Search entire share by name (Enter)…"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '6px 30px 6px 30px', borderRadius: 8, border: '1px solid #1e293b', backgroundColor: '#0b1322', color: '#e5e7eb', fontSize: 12 }} />
                {searchQ && (
                  <span onClick={clearSearch} style={{ position: 'absolute', right: 8, top: 7, cursor: 'pointer', color: '#64748b', display: 'inline-flex' }}>
                    <X style={{ width: 14, height: 14 }} />
                  </span>
                )}
              </div>
              {searching && <RefreshCw style={{ width: 14, height: 14, color: '#93c5fd', animation: 'spin 1s linear infinite' }} />}
              {searchRes && <span style={{ color: '#6b7280', fontSize: 11 }}>{searchRes.count}{searchRes.truncated ? '+' : ''} result{searchRes.count !== 1 ? 's' : ''}</span>}
            </div>
          )}

          {/* bulk-action bar */}
          {mode === 'files' && selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: '#0b1322', border: '1px solid #1d4ed8', borderRadius: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#93c5fd', fontSize: 12, fontWeight: 700 }}>{selected.size} selected</span>
              <button onClick={() => doBulkMove([...selected])} disabled={busy} style={bulkBtn('#34d399')}>
                <FolderInput style={{ width: 13, height: 13 }} /> Move
              </button>
              <button onClick={() => doDelete([...selected])} disabled={busy} style={bulkBtn('#f87171')}>
                <Trash2 style={{ width: 13, height: 13 }} /> Delete
              </button>
              <button onClick={() => [...selected].forEach((p) => window.open(`/api/docs/download?path=${encodeURIComponent(p)}`, '_blank'))} style={bulkBtn('#38bdf8')}>
                <Download style={{ width: 13, height: 13 }} /> Download
              </button>
              <button onClick={() => setSelected(new Set())} style={{ ...bulkBtn('#94a3b8'), marginLeft: 'auto' }}>Clear</button>
            </div>
          )}
          {/* breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 12, fontFamily: 'monospace' }}>
            {crumbs.map((c, i) => (
              <span key={c.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {i > 0 && <span style={{ color: '#475569' }}>/</span>}
                <span onClick={() => setCurrentPath(c.path)} style={{ cursor: 'pointer', color: i === crumbs.length - 1 ? '#fff' : '#60a5fa' }}>{c.name}</span>
              </span>
            ))}
          </div>
        </div>

        {/* body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          {error && <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 10 }}>{error}</div>}

          {mode === 'post' ? (
            <PostPane dir={currentPath} />
          ) : viewer ? (
            <FileViewer viewer={viewer} onClose={() => setViewer(null)}
              onOpenPost={() => { setViewer(null); setMode('post'); }} />
          ) : searchRes ? (
            <SearchResults res={searchRes} onOpenDir={(p) => { clearSearch(); setCurrentPath(p); }}
              onOpenFile={(p) => { clearSearch(); openFile(p); }} />
          ) : (
            <>
              {showUsage && <UsagePanel dir={currentPath} onOpenDir={setCurrentPath} />}
              <PinnedBar pins={pins} onOpenDir={setCurrentPath} onUnpin={togglePin} />
              <DirOverview info={dirInfo} />
              <FileTable listing={listing} onOpenDir={setCurrentPath} onOpenFile={openFile}
                onRename={doRename} onMove={doMove}
                selected={selected} onToggleSel={toggleSel}
                pins={pins} onTogglePin={togglePin} onDelete={doDelete} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const copyPath = (p) => { try { navigator.clipboard?.writeText(p); } catch { /* ignore */ } };

function FileTable({ listing, onOpenDir, onOpenFile, onRename, onMove,
                    selected, onToggleSel, pins, onTogglePin, onDelete }) {
  if (!listing) return <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>;
  const entries = listing.entries || [];
  const allSel = entries.length > 0 && entries.every((e) => selected.has(e.path));
  const toggleAll = () => entries.forEach((e) => {
    if (allSel) { if (selected.has(e.path)) onToggleSel(e.path); }
    else if (!selected.has(e.path)) onToggleSel(e.path);
  });
  return (
    <>
      {listing.truncated && <div style={{ color: '#facc15', fontSize: 11, marginBottom: 8 }}>Showing first 5000 entries (folder truncated).</div>}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'left' }}>
            <th style={{ padding: '4px 8px', width: 26 }}>
              <input type="checkbox" checked={allSel} onChange={toggleAll}
                title="Select all" style={{ cursor: 'pointer', width: 14, height: 14 }} />
            </th>
            <th style={{ padding: '4px 8px' }}>Name</th>
            <th style={{ padding: '4px 8px', width: 90 }}>Size</th>
            <th style={{ padding: '4px 8px', width: 160 }}>Modified</th>
            <th style={{ padding: '4px 8px', width: 170 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && <tr><td colSpan={5} style={{ color: '#6b7280', padding: 12 }}>Empty folder.</td></tr>}
          {entries.map((e) => {
            const sel = selected.has(e.path);
            const pinned = e.type === 'dir' && pins.includes(e.path);
            return (
            <tr key={e.path} style={{ borderTop: '1px solid #1e293b', background: sel ? '#0b1e3a' : 'transparent' }}>
              <td style={{ padding: '6px 8px' }}>
                <input type="checkbox" checked={sel} onChange={() => onToggleSel(e.path)}
                  style={{ cursor: 'pointer', width: 14, height: 14 }} />
              </td>
              <td style={{ padding: '6px 8px' }}>
                <span onClick={() => (e.type === 'dir' ? onOpenDir(e.path) : onOpenFile(e.path))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: e.type === 'dir' ? '#e5e7eb' : '#cbd5e1' }}>
                  {e.type === 'dir' ? <Folder style={{ width: 15, height: 15, color: '#f59e0b' }} /> : <File style={{ width: 15, height: 15, color: '#64748b' }} />}
                  {e.name}
                  {e.type === 'file' && <KindBadge kind={e.kind} />}
                </span>
              </td>
              <td style={{ padding: '6px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>{fmtSize(e.size)}</td>
              <td style={{ padding: '6px 8px', color: '#94a3b8', fontSize: 11 }}>{fmtTime(e.mtime)}</td>
              <td style={{ padding: '6px 8px' }}>
                <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                  {e.type === 'dir' && (
                    <span title={pinned ? 'Unpin' : 'Pin for quick access'} onClick={() => onTogglePin(e.path)}
                      style={{ color: pinned ? '#fbbf24' : '#475569', cursor: 'pointer', display: 'inline-flex' }}>
                      <Star style={{ width: 15, height: 15, fill: pinned ? '#fbbf24' : 'none' }} /></span>
                  )}
                  {e.type === 'file' && (
                    <a href={`/api/docs/download?path=${encodeURIComponent(e.path)}`} title="Download"
                      style={{ color: '#38bdf8', display: 'inline-flex' }}><Download style={{ width: 15, height: 15 }} /></a>
                  )}
                  <span title="Copy path" onClick={() => copyPath(e.path)} style={{ color: '#64748b', cursor: 'pointer', display: 'inline-flex' }}><Copy style={{ width: 14, height: 14 }} /></span>
                  <span title="Rename" onClick={() => onRename(e)} style={{ color: '#a78bfa', cursor: 'pointer', display: 'inline-flex' }}><Edit2 style={{ width: 15, height: 15 }} /></span>
                  <span title="Move" onClick={() => onMove(e)} style={{ color: '#34d399', cursor: 'pointer', display: 'inline-flex' }}><FolderInput style={{ width: 15, height: 15 }} /></span>
                  <span title="Delete" onClick={() => onDelete(e.path)} style={{ color: '#f87171', cursor: 'pointer', display: 'inline-flex' }}><Trash2 style={{ width: 15, height: 15 }} /></span>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}

// Quick-access bar of pinned folders (localStorage).
function PinnedBar({ pins, onOpenDir, onUnpin }) {
  if (!pins || pins.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      <span style={{ color: '#6b7280', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>Pinned</span>
      {pins.map((p) => (
        <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#0b1322', border: '1px solid #334155', borderRadius: 999, padding: '3px 6px 3px 10px', fontSize: 12 }}>
          <Star style={{ width: 12, height: 12, color: '#fbbf24', fill: '#fbbf24' }} />
          <span onClick={() => onOpenDir(p)} title={p} style={{ color: '#e5e7eb', cursor: 'pointer', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.split('/').pop()}</span>
          <span onClick={() => onUnpin(p)} title="Unpin" style={{ color: '#64748b', cursor: 'pointer', display: 'inline-flex' }}><X style={{ width: 12, height: 12 }} /></span>
        </span>
      ))}
    </div>
  );
}

// Whole-share search results.
function SearchResults({ res, onOpenDir, onOpenFile }) {
  const hits = res.hits || [];
  if (hits.length === 0) return <div style={{ color: '#6b7280', fontSize: 13, padding: 12 }}>No matches.</div>;
  return (
    <div>
      {res.truncated && <div style={{ color: '#facc15', fontSize: 11, marginBottom: 8 }}>Showing first {hits.length} matches (refine the query for more).</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {hits.map((h) => (
          <div key={h.path} onClick={() => (h.dir ? onOpenDir(h.path) : onOpenFile(h.path))}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', borderBottom: '1px solid #101a2e' }}
            onMouseEnter={(ev) => { ev.currentTarget.style.background = '#0d1526'; }}
            onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}>
            {h.dir ? <Folder style={{ width: 15, height: 15, color: '#f59e0b', flexShrink: 0 }} /> : <File style={{ width: 15, height: 15, color: '#64748b', flexShrink: 0 }} />}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ color: '#e5e7eb', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                {h.name}{!h.dir && <KindBadge kind={h.kind} />}
              </div>
              <div style={{ color: '#64748b', fontSize: 10, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.rel}</div>
            </div>
            {!h.dir && <span style={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace', flexShrink: 0 }}>{fmtSize(h.size)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Disk-usage panel: fs free/used bar + largest immediate children (polls while du runs).
function UsagePanel({ dir, onOpenDir }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get('/api/docs/usage', { params: { path: dir } });
        if (cancelled) return;
        if (r.data?.success) {
          setData(r.data);
          if (r.data.computing && !pollRef.current) {
            pollRef.current = setInterval(fetchOnce, 3000);
          } else if (!r.data.computing && pollRef.current) {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        } else setErr(r.data?.error || 'usage failed');
      } catch (e) { if (!cancelled) setErr(e.response?.data?.error || e.message); }
    };
    setData(null); setErr(null); fetchOnce();
    return () => { cancelled = true; if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [dir]);

  const fs = data?.fs;
  const maxChild = data?.children?.[0]?.size || 1;
  return (
    <div style={{ background: '#0b1322', border: '1px solid #1e293b', borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <HardDrive style={{ width: 15, height: 15, color: '#fbbf24' }} />
        <span style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 700 }}>Disk usage</span>
        {data?.computing && <span style={{ color: '#93c5fd', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 5 }}><RefreshCw style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> calculating folder sizes…</span>}
      </div>
      {err && <div style={{ color: '#fca5a5', fontSize: 12 }}>{err}</div>}
      {fs && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
            <span>{fmtSize(fs.used)} used of {fmtSize(fs.total)}</span>
            <span style={{ color: fs.usedPct >= 90 ? '#f87171' : fs.usedPct >= 75 ? '#fbbf24' : '#34d399' }}>{fmtSize(fs.free)} free ({100 - fs.usedPct}%)</span>
          </div>
          <div style={{ height: 8, borderRadius: 6, background: '#1e293b', overflow: 'hidden' }}>
            <div style={{ width: `${fs.usedPct}%`, height: '100%', background: fs.usedPct >= 90 ? '#ef4444' : fs.usedPct >= 75 ? '#f59e0b' : '#3b82f6' }} />
          </div>
        </div>
      )}
      <div style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
        Largest items in {dir.split('/').pop() || 'shared'}
      </div>
      {(!data?.children || data.children.length === 0) ? (
        <div style={{ color: '#6b7280', fontSize: 12 }}>{data?.computing ? 'Computing…' : 'Nothing to show.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.children.map((c) => (
            <div key={c.path} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span onClick={() => c.dir && onOpenDir(c.path)}
                style={{ width: 200, flexShrink: 0, color: c.dir ? '#93c5fd' : '#cbd5e1', cursor: c.dir ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.dir ? <Folder style={{ width: 13, height: 13, color: '#f59e0b' }} /> : <File style={{ width: 13, height: 13, color: '#64748b' }} />}
                {c.name}
              </span>
              <div style={{ flex: 1, height: 6, borderRadius: 4, background: '#111c30', overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(2, (c.size / maxChild) * 100)}%`, height: '100%', background: c.dir ? '#6366f1' : '#475569' }} />
              </div>
              <span style={{ width: 74, textAlign: 'right', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>{fmtSize(c.size)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FileViewer({ viewer, onClose, onOpenPost }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => { copyPath(viewer.path); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const isResult = viewer.preview?.kind === 'netcdf';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <File style={{ width: 15, height: 15, color: '#64748b' }} />
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{viewer.name || viewer.path?.split('/').pop()}</span>
        {viewer.size != null && <span style={{ color: '#6b7280', fontSize: 11 }}>{fmtSize(viewer.size)}</span>}
        <div style={{ flex: 1 }} />
        {isResult && onOpenPost && (
          <button onClick={onOpenPost} title="Open post-processing for this folder"
            style={{ background: '#0b1322', border: '1px solid #334155', color: '#a78bfa', fontSize: 12, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ClipboardCheck style={{ width: 14, height: 14 }} /> Open in Post
          </button>
        )}
        <button onClick={doCopy} title="Copy path" style={{ background: 'none', border: 'none', color: copied ? '#34d399' : '#94a3b8', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {copied ? <Check style={{ width: 14, height: 14 }} /> : <Copy style={{ width: 14, height: 14 }} />} {copied ? 'Copied' : 'Copy path'}
        </button>
        {viewer.path && <a href={`/api/docs/download?path=${encodeURIComponent(viewer.path)}`} style={{ color: '#38bdf8', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Download style={{ width: 14, height: 14 }} /> Download</a>}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'inline-flex' }}><X style={{ width: 16, height: 16 }} /></button>
      </div>
      {viewer.loading ? <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>
        : viewer.error ? <div style={{ color: '#fca5a5', fontSize: 12 }}>{viewer.error}</div>
        : <PreviewBody viewer={viewer} />}
    </div>
  );
}

const PRE_STYLE = {
  margin: 0, padding: 12, backgroundColor: '#0b1322', border: '1px solid #1e293b', borderRadius: 8,
  color: '#cbd5e1', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: '60vh', overflow: 'auto',
};

function PreviewBody({ viewer }) {
  const kind = viewer.preview?.kind || (viewer.binary ? 'binary' : 'text');

  if (kind === 'image') {
    return <img src={`/api/docs/download?path=${encodeURIComponent(viewer.path)}`} alt={viewer.name}
      style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #1e293b' }} />;
  }
  if (kind === 'netcdf') {
    return <div style={{ color: '#9ca3af', fontSize: 13, padding: 12, backgroundColor: '#0b1322', border: '1px solid #1e293b', borderRadius: 8 }}>
      <div style={{ color: '#22d3ee', fontWeight: 600, marginBottom: 4 }}>PowerFLOW result file ({fmtSize(viewer.size)})</div>
      {viewer.preview?.note || 'Binary measurement/result data. Open in PowerVIZ/Insight, or use the Post tab for extracted loads.'}
    </div>;
  }
  if (kind === 'binary') {
    return <div style={{ color: '#6b7280', fontSize: 13 }}>Binary file ({fmtSize(viewer.size)}) — use Download to retrieve it.</div>;
  }
  if (kind === 'table') {
    return <TablePreview preview={viewer.preview} />;
  }
  if (kind === 'log') {
    const { highlights = [], errors = [], tail = '' } = viewer.preview;
    return (
      <div>
        {highlights.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            {highlights.map((h, i) => (
              <span key={i} style={{ fontSize: 11, color: '#9ca3af', background: '#0b1322', border: '1px solid #1e293b', borderRadius: 6, padding: '3px 8px' }}>
                {h.label}: <b style={{ color: '#e5e7eb' }}>{h.value}</b>
              </span>
            ))}
          </div>
        )}
        {errors.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: '#f87171', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>⚠ {errors.length} error/warning line(s)</div>
            <pre style={{ ...PRE_STYLE, color: '#fca5a5', maxHeight: '25vh' }}>{errors.join('\n')}</pre>
          </div>
        )}
        <div style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Tail</div>
        <pre style={PRE_STYLE}>{tail}</pre>
      </div>
    );
  }
  // default: text
  return (
    <>
      {viewer.truncated && <div style={{ color: '#facc15', fontSize: 11, marginBottom: 6 }}>Preview truncated at 512 KB.</div>}
      <pre style={PRE_STYLE}>{viewer.text}</pre>
    </>
  );
}

const isNumericCol = (rows, ci) => {
  let n = 0, ok = 0;
  for (const r of rows) { const v = r[ci]; if (v === undefined || v === '') continue; n++; if (Number.isFinite(parseFloat(v))) ok++; }
  return n > 0 && ok / n > 0.8;
};

// Table preview with a Table/Chart toggle. Chart plots any numeric column
// against another (e.g. force vs timestep) so you can eyeball convergence.
function TablePreview({ preview }) {
  const { columns = [], rows = [], rowCount } = preview || {};
  const numericCols = useMemo(() => columns.map((_, ci) => isNumericCol(rows, ci)), [columns, rows]);
  const firstNum = numericCols.indexOf(true);
  const lastNum = numericCols.lastIndexOf(true);
  const [view, setView] = useState('table');
  const [xCol, setXCol] = useState(firstNum >= 0 ? firstNum : 0);
  const [yCol, setYCol] = useState(lastNum >= 0 ? lastNum : columns.length - 1);
  const canChart = numericCols.some(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #334155' }}>
          {['table', 'chart'].map((v) => (
            <button key={v} onClick={() => setView(v)} disabled={v === 'chart' && !canChart}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', border: 'none', cursor: (v === 'chart' && !canChart) ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, background: view === v ? '#2563eb' : '#0b1322', color: view === v ? '#fff' : (v === 'chart' && !canChart) ? '#475569' : '#9ca3af' }}>
              {v === 'table' ? <FileText style={{ width: 12, height: 12 }} /> : <BarChart2 style={{ width: 12, height: 12 }} />}
              {v === 'table' ? 'Table' : 'Chart'}
            </button>
          ))}
        </div>
        {view === 'chart' && canChart && (
          <>
            <label style={{ color: '#6b7280', fontSize: 11 }}>X
              <select value={xCol} onChange={(e) => setXCol(+e.target.value)} style={selStyle}>
                {columns.map((c, i) => <option key={i} value={i}>{c}</option>)}
              </select>
            </label>
            <label style={{ color: '#6b7280', fontSize: 11 }}>Y
              <select value={yCol} onChange={(e) => setYCol(+e.target.value)} style={selStyle}>
                {columns.map((c, i) => numericCols[i] && <option key={i} value={i}>{c}</option>)}
              </select>
            </label>
          </>
        )}
        {rowCount > rows.length && <span style={{ color: '#facc15', fontSize: 11 }}>first {rows.length} of {rowCount} rows</span>}
      </div>

      {view === 'chart' && canChart ? (
        <TableChart columns={columns} rows={rows} xCol={xCol} yCol={yCol} />
      ) : (
        <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #1e293b', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'monospace' }}>
            <thead><tr>{columns.map((c, i) => (
              <th key={i} style={{ position: 'sticky', top: 0, background: '#0b1322', color: '#93c5fd', textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>{c}</th>
            ))}</tr></thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={ri} style={{ background: ri % 2 ? '#0d1526' : 'transparent' }}>
                {r.map((cell, ci) => <td key={ci} style={{ textAlign: 'right', padding: '4px 10px', color: '#cbd5e1', whiteSpace: 'nowrap' }}>{cell}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const selStyle = { marginLeft: 5, background: '#0b1322', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 5, fontSize: 11, padding: '3px 6px', maxWidth: 200 };

function TableChart({ columns, rows, xCol, yCol }) {
  const W = 720, H = 300, pad = { l: 64, r: 16, t: 14, b: 34 };
  const pts = [];
  rows.forEach((r, i) => {
    const yv = parseFloat(r[yCol]);
    if (!Number.isFinite(yv)) return;
    const xv = parseFloat(r[xCol]);
    pts.push({ x: Number.isFinite(xv) ? xv : i, y: yv });
  });
  if (pts.length < 2) return <div style={{ color: '#6b7280', fontSize: 12, padding: 12 }}>Not enough numeric data to plot.</div>;
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  let xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
  if (xmin === xmax) xmax = xmin + 1;
  if (ymin === ymax) { ymin -= 1; ymax += 1; }
  const pyPad = (ymax - ymin) * 0.05; ymin -= pyPad; ymax += pyPad;
  const sx = (x) => pad.l + ((x - xmin) / (xmax - xmin)) * (W - pad.l - pad.r);
  const sy = (y) => H - pad.b - ((y - ymin) / (ymax - ymin)) * (H - pad.t - pad.b);
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ymin + f * (ymax - ymin));
  const xTicks = [0, 0.5, 1].map((f) => xmin + f * (xmax - xmin));
  const fmtN = (v) => (Math.abs(v) >= 1000 || (Math.abs(v) < 0.01 && v !== 0)) ? v.toExponential(2) : v.toFixed(Math.abs(v) < 10 ? 4 : 1);
  return (
    <div style={{ overflowX: 'auto', border: '1px solid #1e293b', borderRadius: 8, background: '#0b1322', padding: 8 }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 480, height: 'auto', display: 'block' }}>
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={pad.l} y1={sy(t)} x2={W - pad.r} y2={sy(t)} stroke="#1e293b" strokeWidth="1" />
            <text x={pad.l - 6} y={sy(t) + 3} textAnchor="end" fill="#64748b" fontSize="10" fontFamily="monospace">{fmtN(t)}</text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={i} x={sx(t)} y={H - pad.b + 16} textAnchor="middle" fill="#64748b" fontSize="10" fontFamily="monospace">{fmtN(t)}</text>
        ))}
        <path d={path} fill="none" stroke="#22d3ee" strokeWidth="1.6" />
        <text x={(pad.l + W - pad.r) / 2} y={H - 4} textAnchor="middle" fill="#93c5fd" fontSize="11">{columns[xCol]}</text>
        <text x={14} y={(pad.t + H - pad.b) / 2} textAnchor="middle" fill="#93c5fd" fontSize="11" transform={`rotate(-90 14 ${(pad.t + H - pad.b) / 2})`}>{columns[yCol]}</text>
      </svg>
    </div>
  );
}
